import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ArrowLeft, User, Gift, Loader2 } from "lucide-react";
import { formatPhoneForDisplay } from "@/utils/phoneFormat";

export default function CustomerDetail({ phone, authAxios, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [couponCode, setCouponCode] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try { const res = await authAxios.get(`/customers/${phone}`); setData(res.data); } catch {}
      setLoading(false);
    })();
  }, [authAxios, phone]);

  const toggleOptOut = async () => {
    try {
      const newVal = !data.customer.opt_out_mass_messages;
      await authAxios.patch(`/customers/${phone}`, { opt_out_mass_messages: newVal });
      setData(p => ({ ...p, customer: { ...p.customer, opt_out_mass_messages: newVal } }));
      toast.success(newVal ? "Mensajes masivos desactivados" : "Mensajes masivos activados");
    } catch { toast.error("Error"); }
  };

  const sendCoupon = async () => {
    if (!couponCode.trim()) return;
    setSending(true);
    try {
      await authAxios.post(`/customers/${phone}/send-coupon`, { coupon_code: couponCode.trim() });
      toast.success("Cupón enviado por WhatsApp!");
      setCouponCode("");
    } catch { toast.error("Error enviando cupón"); }
    setSending(false);
  };

  if (loading) return <div className="absolute inset-0 bg-white z-50 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;
  if (!data) return null;
  const c = data.customer;

  return (
    <div className="absolute inset-0 bg-white z-50 flex flex-col" data-testid="customer-detail">
      <div className="bg-emerald-600 text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2"><button onClick={onClose}><ArrowLeft className="w-5 h-5" /></button><h2 className="font-bold text-sm">Ficha del Cliente</h2></div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center"><User className="w-7 h-7 text-emerald-600" /></div>
          <div>
            <h3 className="font-bold text-lg">{c.name || "Sin nombre"}</h3>
            <p className="text-sm text-gray-500">{formatPhoneForDisplay(c.phone || phone, "CustomerDetail")}</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-emerald-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-emerald-700">{c.total_orders || 0}</p>
            <p className="text-[10px] text-gray-500 uppercase">Pedidos</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-blue-700">{(c.total_spent || 0).toFixed(0)}€</p>
            <p className="text-[10px] text-gray-500 uppercase">Gastado</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-amber-700">{c.points || 0}</p>
            <p className="text-[10px] text-gray-500 uppercase">Puntos</p>
          </div>
        </div>
        <div className="flex items-center justify-between border rounded-lg p-3">
          <div>
            <p className="text-sm font-medium">Recibir mensajes masivos</p>
            <p className="text-xs text-gray-400">{c.opt_out_mass_messages ? "Desactivado" : "Activado"}</p>
          </div>
          <Switch checked={!c.opt_out_mass_messages} onCheckedChange={toggleOptOut} />
        </div>
        <div className="border rounded-lg p-3 space-y-2">
          <p className="text-sm font-bold">Enviar Cupón por WhatsApp</p>
          <div className="flex gap-2">
            <Input value={couponCode} onChange={e => setCouponCode(e.target.value)} placeholder="Código del cupón" className="flex-1" data-testid="send-coupon-input" />
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 gap-1" onClick={sendCoupon} disabled={sending} data-testid="send-coupon-btn">
              {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Gift className="w-3 h-3" />} Enviar
            </Button>
          </div>
          <p className="text-xs text-gray-400">Se enviará un mensaje personalizado con el cupón y el historial del cliente.</p>
        </div>
        {c.source && <p className="text-xs text-gray-400">Canal: <span className="font-medium capitalize">{c.source}</span></p>}
        {c.first_order_at && <p className="text-xs text-gray-400">Primer pedido: {new Date(c.first_order_at).toLocaleDateString()}</p>}
        {c.last_order_at && <p className="text-xs text-gray-400">Último pedido: {new Date(c.last_order_at).toLocaleDateString()}</p>}
        {data.orders?.length > 0 && (
          <div>
            <h4 className="font-bold text-sm mb-2">Últimos pedidos</h4>
            <div className="space-y-2">
              {data.orders.slice(0, 10).map(o => (
                <div key={o.id} className="flex items-center justify-between border rounded p-2 text-sm">
                  <div>
                    <p className="font-medium">#{o.id?.substring(0, 8)}</p>
                    <p className="text-xs text-gray-400">{new Date(o.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">{(o.total || 0).toFixed(2)}€</p>
                    <Badge className="text-[9px]" variant="outline">{o.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
